<template>
  <canvas ref="canvas" class="mist-bg"></canvas>
</template>

<script>
/**
 * MistBackground — 全屏底层流动薄雾背景
 *
 * 多层半透明暗色云雾缓慢平移融合，朦胧柔和
 * 没有锐利线条，低透明度，营造安静朦胧气态氛围
 * 使用径向渐变云团 + 多层不同速度平移 + 加色叠加
 * requestAnimationFrame 驱动，resize 自适应
 */
export default {
  name: 'MistBackground',
  data () {
    return {
      rafId: null,
      w: 0,
      h: 0,
      dpr: 1,
      resizeHandler: null,
      time: 0,
      // 云雾层配置
      layers: []
    }
  },
  mounted () {
    this.resize()
    this.resizeHandler = () => this.resize()
    window.addEventListener('resize', this.resizeHandler)
    this.initLayers()
    this.animate()
  },
  beforeUnmount () {
    if (this.rafId) cancelAnimationFrame(this.rafId)
    if (this.resizeHandler) window.removeEventListener('resize', this.resizeHandler)
  },
  methods: {
    resize () {
      this.dpr = Math.min(window.devicePixelRatio || 1, 2)
      this.w = window.innerWidth
      this.h = window.innerHeight
      const c = this.$refs.canvas
      if (!c) return
      c.width = this.w * this.dpr
      c.height = this.h * this.dpr
      c.style.width = this.w + 'px'
      c.style.height = this.h + 'px'
      const ctx = c.getContext('2d')
      ctx.scale(this.dpr, this.dpr)
    },
    initLayers () {
      // 多层云雾：不同位置、大小、速度、色相
      // 色调用暗灰蓝/暗灰紫，极低饱和
      const configs = [
        { x: 0.15, y: 0.30, r: 500, vx: 0.08, vy: 0.03, color: [60, 75, 100], alpha: 0.10 },
        { x: 0.70, y: 0.20, r: 450, vx: -0.06, vy: 0.04, color: [55, 65, 95], alpha: 0.09 },
        { x: 0.40, y: 0.60, r: 600, vx: 0.05, vy: -0.02, color: [50, 60, 85], alpha: 0.11 },
        { x: 0.85, y: 0.75, r: 480, vx: -0.07, vy: 0.03, color: [65, 55, 85], alpha: 0.08 },
        { x: 0.10, y: 0.85, r: 420, vx: 0.04, vy: -0.03, color: [45, 55, 75], alpha: 0.09 },
        { x: 0.55, y: 0.10, r: 380, vx: -0.05, vy: 0.02, color: [60, 70, 90], alpha: 0.07 },
        { x: 0.30, y: 0.45, r: 520, vx: 0.03, vy: 0.04, color: [50, 65, 80], alpha: 0.08 }
      ]
      this.layers = configs.map(cfg => ({
        x: cfg.x * this.w,
        y: cfg.y * this.h,
        r: cfg.r,
        vx: cfg.vx,
        vy: cfg.vy,
        color: cfg.color,
        alpha: cfg.alpha,
        // 呼吸相位
        phase: Math.random() * Math.PI * 2
      }))
    },
    animate () {
      this.rafId = requestAnimationFrame(this.animate)
      const c = this.$refs.canvas
      if (!c) return
      const ctx = c.getContext('2d')
      ctx.clearRect(0, 0, this.w, this.h)

      this.time += 0.003

      // 逐层绘制径向渐变云雾
      this.layers.forEach((layer) => {
        // 缓慢平移
        layer.x += layer.vx
        layer.y += layer.vy

        // 边界回绕：超出范围后从另一侧回来
        const margin = layer.r
        if (layer.x < -margin) layer.x = this.w + margin
        if (layer.x > this.w + margin) layer.x = -margin
        if (layer.y < -margin) layer.y = this.h + margin
        if (layer.y > this.h + margin) layer.y = -margin

        // 呼吸：半径和透明度极慢起伏
        const breathe = 1 + 0.08 * Math.sin(this.time * 0.5 + layer.phase)
        const r = layer.r * breathe
        const alpha = layer.alpha * (0.85 + 0.15 * Math.sin(this.time * 0.3 + layer.phase))

        const grad = ctx.createRadialGradient(layer.x, layer.y, 0, layer.x, layer.y, r)
        const [cr, cg, cb] = layer.color
        grad.addColorStop(0, `rgba(${cr}, ${cg}, ${cb}, ${alpha})`)
        grad.addColorStop(0.5, `rgba(${cr}, ${cg}, ${cb}, ${alpha * 0.5})`)
        grad.addColorStop(1, `rgba(${cr}, ${cg}, ${cb}, 0)`)

        ctx.fillStyle = grad
        ctx.beginPath()
        ctx.arc(layer.x, layer.y, r, 0, Math.PI * 2)
        ctx.fill()
      })
    }
  }
}
</script>

<style scoped>
.mist-bg {
  position: fixed;
  inset: 0;
  z-index: -1;
  pointer-events: none;
}
</style>
